import { Fragment, ReactNode } from "react";

import type { Cluster } from "~/types";

import { highlightSearchTermsInNodes } from "./searchHighlight";

export type SettingsOverrides = NonNullable<Cluster["settings_overrides"]>;
export type DiffLineType = "context" | "added" | "removed";

export interface DiffLine {
  type: DiffLineType;
  content: string;
}

export interface DiffSection {
  fileName: string;
  status: "added" | "removed" | "changed";
  lines: DiffLine[];
}

interface YamlHighlightOptions {
  reasonPlaceholderStyle?: "background" | "underline";
}

export function getSettingsOverrideFiles(settingsOverrides: SettingsOverrides) {
  return Object.keys(settingsOverrides).sort((a, b) => a.localeCompare(b));
}

export function countSettingsOverrides(settingsOverrides: SettingsOverrides) {
  return Object.values(settingsOverrides).reduce(
    (sum, value) => sum + countSettingsInYaml(value),
    0,
  );
}

export function settingsOverridesEqual(
  left: SettingsOverrides,
  right: SettingsOverrides,
) {
  const leftFiles = getSettingsOverrideFiles(left);
  const rightFiles = getSettingsOverrideFiles(right);

  if (leftFiles.length !== rightFiles.length) {
    return false;
  }

  return leftFiles.every(
    (fileName, index) =>
      fileName === rightFiles[index] && left[fileName] === right[fileName],
  );
}

export function buildSettingsOverridesDiff(
  originalOverrides: SettingsOverrides,
  draftOverrides: SettingsOverrides,
): DiffSection[] {
  const fileNames = Array.from(
    new Set([
      ...getSettingsOverrideFiles(originalOverrides),
      ...getSettingsOverrideFiles(draftOverrides),
    ]),
  ).sort((a, b) => a.localeCompare(b));

  return fileNames.flatMap((fileName): DiffSection[] => {
    const hadFile = Object.prototype.hasOwnProperty.call(
      originalOverrides,
      fileName,
    );
    const hasFile = Object.prototype.hasOwnProperty.call(
      draftOverrides,
      fileName,
    );

    if (!hadFile && hasFile) {
      return [
        {
          fileName,
          status: "added",
          lines: splitDiffLines(draftOverrides[fileName]).map((content) => ({
            type: "added",
            content,
          })),
        },
      ];
    }

    if (hadFile && !hasFile) {
      return [
        {
          fileName,
          status: "removed",
          lines: splitDiffLines(originalOverrides[fileName]).map((content) => ({
            type: "removed",
            content,
          })),
        },
      ];
    }

    if (originalOverrides[fileName] === draftOverrides[fileName]) {
      return [];
    }

    return [
      {
        fileName,
        status: "changed",
        lines: diffTextLines(
          originalOverrides[fileName] ?? "",
          draftOverrides[fileName] ?? "",
        ),
      },
    ];
  });
}

export function diffSectionLabel(section: DiffSection) {
  switch (section.status) {
    case "added":
      return `Added ${section.fileName}`;
    case "removed":
      return `Removed ${section.fileName}`;
    case "changed":
      return `Changed ${section.fileName}`;
  }
}

export function diffLinePrefix(type: DiffLineType) {
  switch (type) {
    case "added":
      return "+";
    case "removed":
      return "-";
    case "context":
      return " ";
  }
}

export function YamlCodeBlock({
  value,
  searchTerms = [],
}: {
  value: string;
  searchTerms?: string[];
}) {
  if (value.length === 0) {
    return (
      <pre className="max-h-[32rem] overflow-auto rounded-md bg-slate-950 p-3 font-mono text-xs text-slate-300">
        <code>
          <span className="text-slate-500">Empty file</span>
        </code>
      </pre>
    );
  }

  const lines = value.split("\n");

  return (
    <pre className="max-h-[32rem] overflow-auto rounded-md bg-slate-950 p-3 font-mono text-xs leading-5 text-slate-200">
      <code>
        {lines.map((line, index) => (
          <Fragment key={index}>
            {highlightSearchTermsInNodes(highlightYamlLine(line), searchTerms)}
            {index < lines.length - 1 ? "\n" : null}
          </Fragment>
        ))}
      </code>
    </pre>
  );
}

export function highlightYamlLine(
  line: string,
  options: YamlHighlightOptions = {},
): ReactNode[] {
  const { body, comment } = splitYamlComment(line);
  const nodes = highlightYamlBody(body);

  if (comment != null) {
    nodes.push(
      <span key="comment" className="text-slate-500">
        {highlightReasonPlaceholder(comment, options)}
      </span>,
    );
  }

  return nodes;
}

function highlightReasonPlaceholder(
  comment: string,
  options: YamlHighlightOptions,
): ReactNode[] {
  const placeholder = "[add reason here]";
  const index = comment.indexOf(placeholder);

  if (index === -1) {
    return [comment];
  }

  return [
    comment.slice(0, index),
    <span
      key="reason-placeholder"
      className={
        options.reasonPlaceholderStyle === "underline"
          ? "text-amber-300 underline decoration-amber-300 decoration-2 underline-offset-2"
          : "rounded bg-amber-300 px-1 font-semibold text-amber-950"
      }
    >
      {placeholder}
    </span>,
    comment.slice(index + placeholder.length),
  ];
}

function countSettingsInYaml(value: string) {
  return value.split("\n").filter((line) => {
    const { body } = splitYamlComment(line);
    return isYamlSettingBody(body.trim());
  }).length;
}

export function isYamlSettingBody(trimmedBody: string) {
  if (
    !trimmedBody ||
    trimmedBody.startsWith("---") ||
    trimmedBody.startsWith("...")
  ) {
    return false;
  }

  const match = trimmedBody.match(/^(?:-\s*)?[^:#\n][^:#\n]*:\s*(.+)$/);
  return match != null && match[1].trim().length > 0;
}

function splitDiffLines(value: string) {
  if (value.length === 0) {
    return [""];
  }

  return value.endsWith("\n")
    ? value.slice(0, -1).split("\n")
    : value.split("\n");
}

function diffTextLines(oldValue: string, newValue: string): DiffLine[] {
  const oldLines = splitDiffLines(oldValue);
  const newLines = splitDiffLines(newValue);

  if (oldLines.length * newLines.length > 40000) {
    return [
      ...oldLines.map((content) => ({ type: "removed" as const, content })),
      ...newLines.map((content) => ({ type: "added" as const, content })),
    ];
  }

  const dp = Array.from({ length: oldLines.length + 1 }, () =>
    Array(newLines.length + 1).fill(0),
  );

  for (let i = oldLines.length - 1; i >= 0; i--) {
    for (let j = newLines.length - 1; j >= 0; j--) {
      dp[i][j] =
        oldLines[i] === newLines[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const lines: DiffLine[] = [];
  let oldIndex = 0;
  let newIndex = 0;

  while (oldIndex < oldLines.length && newIndex < newLines.length) {
    if (oldLines[oldIndex] === newLines[newIndex]) {
      lines.push({ type: "context", content: oldLines[oldIndex] });
      oldIndex++;
      newIndex++;
    } else if (dp[oldIndex + 1][newIndex] >= dp[oldIndex][newIndex + 1]) {
      lines.push({ type: "removed", content: oldLines[oldIndex] });
      oldIndex++;
    } else {
      lines.push({ type: "added", content: newLines[newIndex] });
      newIndex++;
    }
  }

  while (oldIndex < oldLines.length) {
    lines.push({ type: "removed", content: oldLines[oldIndex] });
    oldIndex++;
  }

  while (newIndex < newLines.length) {
    lines.push({ type: "added", content: newLines[newIndex] });
    newIndex++;
  }

  return lines;
}

export function splitYamlComment(line: string) {
  let quote: "'" | '"' | null = null;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const previousChar = line[i - 1];

    if (quote === '"' && char === "\\" && i + 1 < line.length) {
      i++;
      continue;
    }

    if (char === "'" || char === '"') {
      if (quote === char) {
        quote = null;
      } else if (quote == null) {
        quote = char;
      }
      continue;
    }

    if (char === "#" && quote == null && (i === 0 || /\s/.test(previousChar))) {
      return {
        body: line.slice(0, i),
        comment: line.slice(i),
      };
    }
  }

  return { body: line, comment: null };
}

function highlightYamlBody(body: string): ReactNode[] {
  const keyMatch = body.match(/^(\s*(?:-\s*)?)([^:#\n][^:#\n]*?)(\s*):(.*)$/);

  if (!keyMatch) {
    return highlightYamlScalars(body, "body");
  }

  const [, prefix, key, separator, value] = keyMatch;

  return [
    <span key="prefix" className="text-slate-400">
      {prefix}
    </span>,
    <span key="key" className="text-sky-300">
      {key}
    </span>,
    <span key="separator" className="text-slate-400">
      {separator}:
    </span>,
    ...highlightYamlScalars(value, "value"),
  ];
}

function highlightYamlScalars(text: string, keyPrefix: string): ReactNode[] {
  const tokenPattern =
    /("(?:\\.|[^"\\])*"|'(?:''|[^'])*'|\b(?:true|false|null|~)\b|-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)/gi;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(text)) != null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    nodes.push(
      <span
        key={`${keyPrefix}-${match.index}`}
        className={yamlTokenClass(token)}
      >
        {token}
      </span>,
    );
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function yamlTokenClass(token: string) {
  if (token.startsWith('"') || token.startsWith("'")) {
    return "text-emerald-300";
  }

  if (/^(true|false|null|~)$/i.test(token)) {
    return "text-amber-300";
  }

  return "text-purple-300";
}
